<template>
    <b-row class="mb-3">
        <b-card no-body bg-variant="white" class="w-100">
            <template #header>
                <div class="d-flex align-items-center justify-content-between">
                    <h5 class="mb-0">다국어 문자열 검색하기</h5>

                    <div v-if="listLoading">
                        <div class="d-flex align-items-center" style="height: 36px">
                            <b-spinner class="mr-2" variant="primary" style="height: 1rem; width: 1rem;"/>
                            <p class="d-inline-block mb-0">조회중...</p>
                        </div>
                    </div>
                    <div v-else class="d-flex align-items-center">
                        <b-button form="locale-string-search-form"
                                  type="reset"
                                  class="mr-2"
                                  :disabled="listLoading"
                        >
                            {{ $t('초기화') }}
                        </b-button>
                        <b-button
                            variant="primary"
                            :disabled="listLoading"
                            form="locale-string-search-form"
                            type="submit"
                        >
                            {{ $t('검색하기') }}
                        </b-button>
                    </div>
                </div>
            </template>

            <div class="p-3">
                <b-form id="locale-string-search-form"
                        autocomplete="new-password"
                        @submit.stop.prevent="searchData"
                        @reset.prevent="resetSearch"
                >
                    <b-row>
                        <b-col cols="12" md="6" class="mb-3 mb-xl-0">
                            <b-form-group
                                id="fieldset-input-search"
                                label="검색어"
                                label-for="input-search"
                                class="mb-0"
                            >
                                <b-form-input id="input-search"
                                              v-model="searchFormValue.search"
                                              :disabled="listLoading"
                                              placeholder="문자열 키 또는 텍스트 검색"
                                />
                            </b-form-group>
                        </b-col>
                        <b-col cols="12" md="6">
                            <b-form-group
                                id="fieldset-input-locale"
                                label="로케일"
                                label-for="input-locale"
                                class="mb-0"
                            >
                                <b-form-select id="input-locale"
                                               v-model="searchFormValue.locale"
                                               :disabled="listLoading"
                                >
                                    <b-form-select-option v-for="data in localeOptions"
                                                          :key="`${data.text}-${data.value}`"
                                                          :value="data.value">{{ data.text }}
                                    </b-form-select-option>
                                </b-form-select>
                            </b-form-group>
                        </b-col>
                    </b-row>
                </b-form>

            </div>
        </b-card>


        <b-row class="mt-3">
            <b-col cols="12">
                <b-card
                    :class="[{ 'shadow-sm': searchResults || (!listLoading && searchResults) }, 'list-container']"
                    :bg-variant="!searchResults ? 'light' : 'white'"
                    no-body
                >
                    <template #header>
                        <div class="d-flex align-items-center justify-content-between">
                            <div>
                                <h6 class="mb-0">다국어 문자열 목록</h6>
                            </div>
                            <div class="d-flex align-items-center">
                                <b-button variant="outline-primary" size="md"
                                          @click="openConditionModal('add')">
                                    다국어 문자열 추가
                                </b-button>
                            </div>
                        </div>
                    </template>
                    <div class="table-container">
                        <b-table
                            fixed responsive
                            :items="searchResults"
                            :fields="tableColumns"
                            class="mb-0"
                            show-empty
                            :busy="listLoading"
                        >
                            <template #empty>
                                <PageEmptyComponent
                                    :text="$t('조회된 다국어 문자열이 없습니다.')"
                                    :loading="listLoading"
                                    :loadingText="$t('다국어 문자열 목록을 조회 중입니다.')"
                                    :error="listError"
                                    @retry="fetchData"
                                />
                            </template>
                            <template #cell(key)="row">
                                <span v-b-tooltip.hover="get_tooltip_title(row.value)">{{ row.value }}</span>
                            </template>

                            <template #cell(ko)="row">
                                <span v-b-tooltip.hover="get_tooltip_title(row.value)">{{ row.value }}</span>
                            </template>

                            <template #cell(en)="row">
                                <span v-b-tooltip.hover="get_tooltip_title(row.value)">{{ row.value || '-' }}</span>
                            </template>
                            <template #cell(ca)="row">
                                <span v-b-tooltip.hover="get_tooltip_title(row.value)">{{ row.value || '-' }}</span>
                            </template>

                            <template #cell(detail)="data">
                                <b-btn size="sm"
                                       variant="outline-primary"
                                       @click="openConditionModal('detail', data.item)">상세
                                </b-btn>
                            </template>

                            <template #cell(updated_at)="row">
                                {{ get_date(row.value) }}

                            </template>
                            <template #cell(created_at)="row">
                                {{ get_date(row.value) }}
                            </template>

                        </b-table>

                        <div class="pagination-container">
                            <b-pagination
                                ref="pagination"
                                v-model="pagination.currentPage"
                                :total-rows="pagination.totalRows"
                                :per-page="pagination.perPage"
                                first-number
                                last-number
                                :disabled="listLoading"
                                align="fill"
                                size="sm"
                                @change="(page)=>changeQuery({page})"
                            ></b-pagination>
                            <b-form-select
                                v-model="pagination.perPage"
                                class="ml-3 mb-3"
                                style="width: 100px"
                                size="sm"
                                :disabled="listLoading"
                                @change="(pageSize)=>changeQuery({pageSize})"
                            >
                                <b-form-select-option v-for="data in perPageOptions"
                                                      :key="`${data.text}-${data.value}`"
                                                      :value="data.value">{{ data.text }}
                                </b-form-select-option>
                            </b-form-select>

                        </div>
                    </div>
                </b-card>
            </b-col>
        </b-row>

        <LocaleStringModal ref="localeStringModal"
                        :detail-id="detailId"
                        @updateDetailData="updateDetailData"
                        @addData="initPage"
                        @deleteData="initPage"
        />

    </b-row>
</template>

<script>
import {listLocalizedStrings} from '@shared_utils/api/projectSchema';
import {
    getDefaultPagination,
    onTooltipShow
} from '@shared/components/admin/pages/Management/common-data';
import {getErrorMessage} from '@core/utils/api/errorMessage';
import PageEmptyComponent from '@shared/components/admin/components/common/PageEmptyComponent.vue';
import PaginationPageMixin from '@shared/mixin/pagination-page-mixins';
import {
    columns,
    localeOptions,
    searchFormValue
} from '@shared/components/admin/pages/Management/ProjectSchema/LocaleString/data';
import LocaleStringModal
    from '@shared/components/admin/pages/Management/ProjectSchema/LocaleString/LocaleStringModal.vue';

export default {
    name: 'ManageLocaleString',
    components: {
        LocaleStringModal,
        PageEmptyComponent,
    },
    mixins: [PaginationPageMixin({searchForm: searchFormValue, pagination: getDefaultPagination(1, 20)})],
    props: {
        currentTab: {
            type: Number,
            default: 0
        }
    },

    data() {
        return {
            tableColumns: columns,
            detailId: null,
            listError: '',
        };
    },
    computed: {
        localeOptions(){
            return localeOptions;
        }
    },
    watch: {
        '$route': {
            handler() {
                if (this.$route.query.tab === 'localeString') {
                    this.setSearchDataFormQuery();
                    this.fetchData();
                }
            },
            // 직접 URL/콘솔 고급 메뉴 진입은 route 변경이 없어 handler 가 안 돌므로 마운트 시에도 한 번 판정한다
            immediate: true
        }
    },
    methods: {
        onTooltipShow,
        async searchData() {
            this.pagination.currentPage = 1;
            const checkSameQuery = await this.changeQuery({});
            if (checkSameQuery) {
                await this.fetchData();
            }
        },
        initPage() {
            this.resetForm();
            this.setSearchDataFormQuery();
            this.fetchData();
        },
        async fetchData() {
            try {
                this.listLoading = true;
                this.listError = '';

                const form = {};
                if(this.searchFormValue.locale == null){
                    form.search = this.searchFormValue.search;
                }
                else {
                    form[this.searchFormValue.locale] = this.searchFormValue.search;
                }

                const res = await listLocalizedStrings({
                    params: {
                        ...form,
                        page: this.pagination.currentPage,
                        per_page: this.pagination.perPage,
                    },
                });

                if (res.data == null) {
                    this.searchResults = [];
                    this.pagination.totalRows = 0;
                    return;
                }
                const {localized_strings, total, current_page} = res.data;

                this.searchResults = localized_strings.map((item, index) => {
                    const data = {
                        ...item,
                        value: JSON.stringify(item.value),
                        index: total - (this.pagination.perPage * (this.pagination.currentPage - 1) + index),
                    };
                    return data;
                });

                this.pagination.totalRows = total;
                this.pagination.currentPage = null;
                setTimeout(() => {
                    this.pagination.currentPage = Number(current_page);
                });
            } catch (error) {
                // 실패를 '조회된 다국어 문자열이 없습니다'로 보이지 않게 이전 결과를 비우고 표 안에 오류를 띄운다
                this.searchResults = [];
                this.pagination.totalRows = 0;
                if (error?.response?.status === 401) return;
                this.listError = `다국어 문자열 목록을 불러오지 못했습니다. ${getErrorMessage(error, '')}`.trim();
                this.$EventBus.$emit('toast-failure', this.$t('검색 오류'), '다국어 문자열 검색 도중 오류가 발생했습니다. 잠시후 재시도 해주세요.');
            } finally {
                this.listLoading = false;
            }
        },
        async openConditionModal(mode, item) {
            if (mode === 'add') {
                this.detailId = null;
                this.$refs.localeStringModal.changeMode(mode);
                this.$bvModal.show('locale-string-detail-modal');
                return;
            }

            console.log(item);

            this.detailId = item.key;
            this.$refs.localeStringModal.changeMode(mode);
            this.$bvModal.show('locale-string-detail-modal');

        },
        updateDetailData(updatedItem) {
            const idx = this.searchResults.findIndex((item) => {
                return item.key === updatedItem.key;
            });
            this.$set(this.searchResults, idx, {...updatedItem, index: this.searchResults.length - idx});
        }
    }
};
</script>

<style lang="scss">
</style>
